import Image from "next/image";
import { Box, BoxProps, Stack, StackProps, Typography, TypographyProps, useMediaQuery } from "@mui/material";
import { API_URL } from "helpers/common";
import { PageProps } from "pages/_app";
import router from "next/router";
import AppLink from "components/v2/atoms/app-link/AppLink";
import AppIconSocialButton, {
    AppIconSocialButtonProps,
} from "components/v2/atoms/app-social-button/AppIconSocialButton";
import trustIcons from "assets/icons/landing-page/trusticons-mobile.png";
import trustIconsDesktop from "assets/icons/landing-page/trusticons-desktop.png";
import AppSocialButton, { AppSocialButtonProps } from "components/v2/atoms/app-social-button/AppSocialButton";
import header1 from "assets/icons/landing-page/nomi-header1.png";
import header2 from "assets/icons/landing-page/nomi-header2.png";
import { usePartnerId } from "hooks/partner-id";

interface RightPanelProps {
    isMobile: boolean;
    partner: string;
}

interface LeftPanelProps {
    isMobile: boolean;
    partner: string;
}

interface ContentProps {
    partner: string;
}

function Quickbooks(_: PageProps) {
    const { partner } = usePartnerId({ defaultPartner: "nomi" });

    const contentProps: ContentProps = {
        partner: partner,
    };

    return <Content {...contentProps} />;
}

function Content(props: ContentProps) {
    const isMobile = useMediaQuery("(max-width:1040px)");

    const outerContainerProps: BoxProps = {
        sx: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            paddingTop: isMobile ? 0 : "30px",
            boxSizing: "border-box",
            minHeight: isMobile ? "100%" : "80vh",
            height: "auto",
            maxHeight: "auto",
            padding: isMobile ? 2 : "auto",
            minWidth: isMobile ? "375px" : "1044px",
            maxWidth: isMobile ? "650px" : "1044px",
            margin: "auto",
        },
    };

    const stackProps: StackProps = {
        sx: {
            flexDirection: isMobile ? "column" : "row",
        },
        gap: 3,
    };

    const leftStackProps: StackProps = {
        sx: {
            backgroundColor: "#FFFFFF",
            width: isMobile ? "auto" : "550px",
        },
        border: 0,
        borderRadius: 1,
        boxShadow: 1,
        maxWidth: isMobile ? "615px" : "560px",
    };

    const rightStackProps: StackProps = {
        sx: {
            backgroundColor: "#FFFFFF",
            width: "auto",
            marginBottom: "auto",
        },
        border: 0,
        borderRadius: 1,
        boxShadow: 1,
        maxWidth: isMobile ? "615px" : "450px",
    };

    const rightPanelProps: RightPanelProps = {
        isMobile: isMobile,
        partner: props.partner,
    };

    const leftPanelProps: LeftPanelProps = {
        isMobile: isMobile,
        partner: props.partner,
    };

    const trustImageStackProps: StackProps = {
        children: (
            <Image
                src={trustIcons}
                alt="tpreview"
                style={{
                    maxWidth: "100%",
                    height: "auto",
                }}
            />
        ),
        sx: {
            alignItems: "center",
            paddingRight: 4,
            paddingLeft: 4,
            paddingTop: 0,
            paddingBottom: 0,
        },
    };

    const trustImageDesktopStackProps: StackProps = {
        children: (
            <Image
                src={trustIconsDesktop}
                alt="tpreview"
                width={"1070"}
                height={"92"}
                style={{
                    maxWidth: "100%",
                    height: "auto",
                }}
            />
        ),
        sx: {
            alignItems: "center",
            paddingTop: 6,
        },
    };

    return (
        <Box {...outerContainerProps}>
            <Stack>
                <Stack {...stackProps}>
                    <Stack {...leftStackProps}>
                        <LeftPanel {...leftPanelProps} />
                    </Stack>
                    {isMobile && <Stack {...trustImageStackProps}></Stack>}
                    <Stack {...rightStackProps}>
                        <RightPanel {...rightPanelProps} />
                    </Stack>
                </Stack>
                {!isMobile && <Stack {...trustImageDesktopStackProps}></Stack>}
            </Stack>
        </Box>
    );
}

export default Quickbooks;

function LeftPanel(props: LeftPanelProps) {
    const stackProps: StackProps = {
        sx: {},
    };

    const bodyStackProps: StackProps = {
        sx: {
            p: { xs: 3 },
            alignContent: { xs: "center", md: "flex-start" },
            justifyContent: { xs: "center", md: "flex-start" },
            textAlign: "left",
        },
        gap: 2,
    };

    const loginLinkProps: TypographyProps = {
        fontSize: { xs: 14, md: 14 },
        color: "text.disabled",
        sx: {
            textAlign: { xs: "center", md: "left" },
            mb: { xs: 0, md: 2 },
        },
    };

    const buttonStackProps: StackProps = {
        sx: {
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            mt: 1,
            mb: 1,
        },
        gap: { xs: 1, md: 2 },
    };

    const xeroButtonProps: AppIconSocialButtonProps = {
        id: "instant-vat_steps_xero",
        brand: "xero",
        onClick() {
            location.href = API_URL + "/accountant_xero_sso_signup";
        },
    };

    const emailButtonProps: AppSocialButtonProps = {
        id: "open-tax_steps_email_email",
        brand: "emailPrimary",
        onClick() {
            let url = API_URL + "/portal/v2/open-tax/steps/email";
            url += `?company_id=accountant`;
            location.href = url;
        },
        children: "Continue with email",
        variant: "contained",
        sx: {
            alignContent: "center",
            justifyContent: "center",
            paddingLeft: 4,
            width: { xs: "100%", md: 240 },
            backgroundColor: "#FF00AA",
            fontSize: { xs: 14, md: 14 },
        },
    };

    const googleButtonProps: AppIconSocialButtonProps = {
        id: "instant-vat_steps_email_google",
        brand: "google",
        onClick() {
            let url = API_URL + "/google_signup";
            url += `?partner_signup=accountant`;
            location.href = url;
        },
    };

    const microsoftButtonProps: AppIconSocialButtonProps = {
        id: "instant-vat_steps_email_microsoft",
        brand: "microsoft",
        onClick() {
            let url = API_URL + "/microsoft_signup";
            url += `?partner_signup=accountant`;
            location.href = url;
        },
    };

    const footerProps: TypographyProps = {
        sx: {
            mt: { xs: -2, md: -2 },
            p: 2,
            textAlign: { xs: "center", md: "left" },
        },
        color: "#9DA4AF",
        fontSize: { xs: 9, md: 11 },
        children: "As part of our partnership we’ll keep Nomi updated if you move forward with OpenTax.",
    };

    const setupTextProps: TypographyProps = {
        fontSize: { xs: 14, md: 14 },
        fontWeight: 500,
        mt: 0.5,
        mb: 1,
    };

    return (
        <Stack {...stackProps}>
            <Box
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    position: "relative",
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                }}>
                <Image
                    src={header1}
                    alt="header1"
                    style={{
                        objectFit: "cover",
                        borderTopLeftRadius: "8px",
                        borderTopRightRadius: "8px",
                        maxWidth: "100%",
                        height: "auto",
                    }}
                />
            </Box>
            <Stack {...bodyStackProps}>
                <Typography sx={{ fontSize: { xs: 16, md: 16 } }} mb={-1}>
                    Save 30 hours every month
                </Typography>
                <Typography variant="h1">
                    Start tracking your
                    {props.isMobile ? " " : <br />}
                    client’s VAT returns
                </Typography>
                <Typography {...setupTextProps}>
                    Connect your Agent Services Account and get setup in 2 minutes.
                </Typography>
                <Stack {...buttonStackProps}>
                    <AppSocialButton {...emailButtonProps} />
                    <Typography sx={{ fontSize: { xs: 14, md: 14 } }}>or</Typography>
                    <Stack flexDirection={"row"} gap={2}>
                        <AppIconSocialButton {...googleButtonProps} />
                        <AppIconSocialButton {...microsoftButtonProps} />
                        <AppIconSocialButton {...xeroButtonProps} />
                    </Stack>
                </Stack>
                <Typography {...loginLinkProps}>
                    Already have an account?{" "}
                    <AppLink color={"#5C5C5C"} id="login-link" href={API_URL + "/portal/v2/auth/login"}>
                        Log in
                    </AppLink>
                </Typography>
            </Stack>
            <Typography {...footerProps}></Typography>
        </Stack>
    );
}

function RightPanel(_: RightPanelProps) {
    const stackProps: StackProps = {
        sx: {},
    };

    const bodyStackProps: StackProps = {
        sx: {
            paddingBottom: { xs: 2, md: 4 },
            paddingLeft: { xs: 1, md: 2 },
            paddingRight: { xs: 1, md: 2 },
            marginTop: 0.5,
        },
        gap: 2,
    };

    const disabledTextProps: TypographyProps = {
        fontSize: { xs: 14, md: 14 },
        color: "#9DA4AF",
        mt: 2,
    };

    return (
        <Stack {...stackProps}>
            <Box
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    position: "relative",
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                }}>
                <Image
                    src={header2}
                    alt="header2"
                    onClick={() => {
                        router.push(`/v2/open-tax/contact-us`);
                    }}
                    style={{
                        objectFit: "cover",
                        cursor: "pointer",
                        borderTopLeftRadius: "8px",
                        borderTopRightRadius: "8px",
                        maxWidth: "100%",
                        height: "auto",
                    }}
                />
            </Box>
            <Stack {...bodyStackProps}>
                <Typography sx={{ fontSize: { xs: 14, md: 14 } }} lineHeight={2}>
                    <ul className="accelerated-dialog-list">
                        <li>
                            <Typography {...disabledTextProps}>
                                <span style={{ fontWeight: 500, color: "#32373E" }}>VAT tracking:</span> Instantly
                                access live data on VAT payments, reclaim submissions, and approval statuses with HMRC.
                            </Typography>
                        </li>
                        <li>
                            <Typography {...disabledTextProps}>
                                <span style={{ fontWeight: 500, color: "#32373E" }}>Centralised dashboard:</span> View
                                all client information in one place and filter based on different statuses and
                                priorities.
                            </Typography>
                        </li>
                        <li>
                            <Typography {...disabledTextProps}>
                                <span style={{ fontWeight: 500, color: "#32373E" }}>PAYE tracking:</span> Get up-to-date
                                information on PAYE payments, notifications of upcoming payments, and direct debit
                                status.
                            </Typography>
                        </li>
                    </ul>
                </Typography>
            </Stack>
        </Stack>
    );
}
